import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { untilDestroyed } from '@ngneat/until-destroy';
import { DatastoreService } from '@src/app/helpers/datastore/datastore.service';
import { SessionHelper } from '@src/app/helpers/sessionHelper';
import { Task } from '@src/app/models/task';
import { CplListService } from './service/cpl-list.service';
import { FormResponseService } from '@src/app/components/forms/service/form-response.service';
import { FormService } from '@src/app/components/forms/service/form.service';
import { FormTemplate } from '@src/app/components/forms/models/form.model';
import { FormRendererComponent } from '@src/app/components/forms/renderer/form-renderer.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ModalService } from '@src/app/helpers/modal.service';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { UieventhubService } from '@src/app/helpers/uieventhub/uieventhub.service';

declare var $: any; // jQuery declaration for Bootstrap modal events

@Component({
  selector: 'app-cpl-list',
  templateUrl: './cpl-list.component.html',
  styleUrls: ['./cpl-list.component.css', './cpl-list-loader.css'],
  animations: [
    trigger(
      'inbgOutAnimation',
      [
        transition(
          ':enter',
          [
            style({ opacity: 0 }),
            animate('0.2s ease-out',
              style({ opacity: 1 }))
          ]
        ),
        transition(
          ':leave',
          [
            style({ opacity: 1 }),
            animate('0.2s ease-in',
              style({ opacity: 0 }))
          ]
        )
      ]
    )
  ]
})
export class CplListComponent implements OnInit, OnDestroy {
  customerTasks: Task[] = [];
  fileselected: boolean;
  organizationId: string;
  customerId: string;
  projectId: string;
  modalTask: Task;
  filelisttoggle: boolean;
  changeDetector: ChangeDetectorRef;
  contentPrinted: boolean;
  filelistUpdatedvalue: boolean;
  uploadedFiles: Map<string, boolean> = new Map(); // Track which upload fields have files
  isLoading: boolean = true;
  hasRendered: boolean = false;
  formModalLoading: boolean = false; // Track form modal loading state
  constructor(
    private datastore: DatastoreService,
    private cplListService: CplListService,
    private modalservice: ModalService,
    private uieventhub: UieventhubService,
    private formResponseService: FormResponseService,
    private formService: FormService,
    private matDialog: MatDialog,
    private cdRef: ChangeDetectorRef
  ) {
    this.changeDetector = cdRef;
  }
  ngOnInit() {
    this.uieventhub.addLoader('customerlight');
    // Fetch the organizationId, customerId, and projectId from the datastore service
    this.organizationId = SessionHelper.getChoosenOrg();
    this.customerId = JSON.parse(SessionHelper.getCustomer() || '{}').customerId || '';
    // Fetch the customer tasks from the datastore service
    this.customerTasks = this.datastore.customerTasks.getValue();
    this.setProjectContext(this.customerTasks[0]?.projectId || '');

    //Sort by start date and then by sortOrder
    this.customerTasks.sort((a, b) => {
      const dateA = new Date(a.startdate).getTime();
      const dateB = new Date(b.startdate).getTime();

      if (dateA === dateB) {
        return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      }

      return dateA - dateB;
    });
    // Subscribe to the customerTasks observable to get the tasks
    this.datastore.customerTasks.subscribe(ctl => {
      this.customerTasks = ctl;
      this.setProjectContext(this.customerTasks[0]?.projectId || '');

      this.customerTasks.sort((a, b) => {
        const dateA = new Date(a.startdate).getTime();
        const dateB = new Date(b.startdate).getTime();

        if (dateA === dateB) {
          return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        }

        return dateA - dateB;
      });
      this.customerTasks.forEach(task => {
        task.canEdit = task.responsible === SessionHelper.getUserSub();
      });      //console.log('Customer Tasks:', this.customerTasks);
    }
    );

    // Set up modal close event listeners
    this.setupModalEventListeners();

  }

  private sortTasks(tasks: Task[]) {
    const start = (t: Task) => (t.startdate || '').substring(0, 10);
    tasks.sort((a, b) => {
      const sa = start(a);
      const sb = start(b);
      if (sa !== sb) {
        if (!sa) return 1; // tasks without a start date go last
        if (!sb) return -1;
        return sa < sb ? -1 : 1;
      }
      return (a.sortOrder || 0) - (b.sortOrder || 0);
    });
  }

  setupModalEventListeners() {
    // Listen for the upload modal being hidden
    $('#uploadmodal').on('hidden.bs.modal', () => {
      this.resetUploadModalState();
    });
  }

  setProjectContext(projectId: string) {
    this.projectId = projectId;
    SessionHelper.setCurrentProject(projectId);
    let projectList = this.datastore.projectList.getValue();
    let currentProject = projectList.find(proj => proj.projectId === projectId);
    if (currentProject) {
      SessionHelper.setCurrentProjectName(currentProject.title);
      SessionHelper.setCurrentProjectFull(JSON.stringify(currentProject));
    }
    this.datastore.loadProjectDependentData(projectList);
  }

  newFile(file: any) {
    //console.log('New file uploaded:', this.modalTask);
    this.fileselected = true;

    // Update the tracking for the specific upload field
    if (file && file.taskInputName) {
      // File uploaded (either new upload or existing file loaded)
      this.uploadedFiles.set(file.taskInputName, true);
      // Check task completion
      this.checkTaskCompletion();
    } else if (file === null) {
      // File was deleted - this is handled by the onFileDeleted method now
      // but we still need to handle legacy cases where onFileDeleted isn't called
      this.recalculateFileCount();
    }
  }

  // Method to recalculate file count - this is a fallback for when we can't track individual fields
  private recalculateFileCount() {
    // Reset the map and let the filelistUpdated events repopulate it
    this.uploadedFiles.clear();

    // Wait a bit for all components to update, then recheck the completion status
    setTimeout(() => {
      this.checkTaskCompletion();
    }, 100);
  }

  // Method to check if task should be marked as complete
  private checkTaskCompletion() {
    // Count how many upload fields actually have files by checking the current file count
    if (this.modalTask && this.modalTask.uploadFields) {
      this.modalTask.numberoffiles = Array.from(this.uploadedFiles.values()).filter(hasFile => hasFile).length;

      // Check if all required files are uploaded
      if (this.modalTask.numberoffiles >= this.modalTask.uploadFields.length) {
        this.taskDone(this.modalTask);
      }
    }
  }
  openUploadModal(task: Task) {
    // Clear previous upload state
    this.uploadedFiles.clear();
    this.fileselected = false;
    this.contentPrinted = false;
    this.filelistUpdatedvalue = false;

    // Set the new task
    this.modalTask = task;
    this.projectId = task.projectId;

    // Initialize the file counter to 0 when opening the modal
    // The actual count will be determined by the filelistUpdated events
    this.modalTask.numberoffiles = 0;

    // Initialize the tracking map for upload fields
    if (this.modalTask.uploadFields) {
      this.modalTask.uploadFields.forEach(fieldName => {
        this.uploadedFiles.set(fieldName, false);
      });
    }

    //console.log('Opening upload modal for task:', this.modalTask.taskId, 'with fields:', this.modalTask.uploadFields);

    this.modalservice.open('uploadmodal');
    SessionHelper.setCurrentProject(this.projectId);
  }
  taskAction(task: Task) {
    //console.log('Task action triggered for task:', task);
    if (task.taskstate != 'Done') {
      if (task.tasktype === 'form') {
        this.openFormModal(task);
      } else {
        this.taskDone(task);
      }
    }
  };

  filelistUpdated(boolean) {
    this.filelistUpdatedvalue = boolean;
  };

  onFileDeleted(taskInputName: string) {
    // Mark the specific upload field as not having a file
    if (taskInputName) {
      this.uploadedFiles.set(taskInputName, false);
      this.checkTaskCompletion();
    }
  }
  eventAction(boolean) { };
  submitAction(task: Task) {
    //console.log('Submit action triggered for task:', task);
    this.fileselected = true;
    this.modalTask = task;
    //console.log('Submit action triggered for task ID:', this.modalTask.taskId);
  };
  downloadAction(task: Task) {
    // Mark task done
    this.taskDone(task);
  };

  async taskDone(task: Task) {
    task.taskstate = "Done";
    //console.log('Task marked as done');
    let response = await firstValueFrom(this.cplListService.taskDone(task));
    //console.log('Task done response:', response)
  }

  private resolveFormTaskMetadata(task: Task): Task {
    const taskAny = task as any;
    const cachedTask = (this.datastore.taskList.getValue() || []).find(t => t.taskId === task.taskId);
    const cachedTaskAny = cachedTask as any;

    const resolvedFormTemplateId = task.formTemplateId
      || taskAny.form_template_id
      || taskAny.templateId
      || taskAny.template_id
      || cachedTask?.formTemplateId
      || cachedTaskAny?.form_template_id
      || cachedTaskAny?.templateId
      || cachedTaskAny?.template_id;

    const rawTemplateVersion = task.formTemplateVersion
      ?? taskAny.form_template_version
      ?? taskAny.templateVersion
      ?? taskAny.template_version
      ?? cachedTask?.formTemplateVersion
      ?? cachedTaskAny?.form_template_version
      ?? cachedTaskAny?.templateVersion
      ?? cachedTaskAny?.template_version;

    const resolvedFormTemplateVersion = rawTemplateVersion === undefined || rawTemplateVersion === null || rawTemplateVersion === ''
      ? undefined
      : Number(rawTemplateVersion);

    const resolvedFormTemplateName = task.formTemplateName
      || taskAny.form_template_name
      || taskAny.templateName
      || taskAny.template_name
      || cachedTask?.formTemplateName
      || cachedTaskAny?.form_template_name
      || cachedTaskAny?.templateName
      || cachedTaskAny?.template_name;

    return {
      ...task,
      formTemplateId: resolvedFormTemplateId,
      formTemplateVersion: resolvedFormTemplateVersion,
      formTemplateName: resolvedFormTemplateName,
    } as Task;
  }

  openFormModal(task: Task) {
    const resolvedTask = this.resolveFormTaskMetadata(task);

    console.log('openFormModal called with task:', {
      taskId: resolvedTask.taskId,
      formTemplateId: resolvedTask.formTemplateId,
      formTemplateVersion: resolvedTask.formTemplateVersion,
      tasktype: resolvedTask.tasktype
    });
    
    if (!resolvedTask.formTemplateId || resolvedTask.formTemplateVersion == null || Number.isNaN(resolvedTask.formTemplateVersion)) {
      console.error('Task missing form template reference');
      return;
    }

    // Show loader
    this.formModalLoading = true;

    // Extract base template ID (strip version suffix if present)
    const baseTemplateId = resolvedTask.formTemplateId.replace(/_v\d+$/, '');
    console.log('Base template ID after stripping version:', baseTemplateId);

    // Fetch the form template
    console.log('[CplList] About to subscribe to getTemplate');
    const subscription = this.formService.getTemplate(this.organizationId, baseTemplateId)
      .subscribe({
        next: (templates: FormTemplate[]) => {
          console.log('[CplList] Form templates received:', templates);
          console.log('[CplList] Looking for templateId:', resolvedTask.formTemplateId, 'version:', resolvedTask.formTemplateVersion);
          
          // Prefer the exact version linked to the task; fallback to first
          const template = templates.find(t => t.version === resolvedTask.formTemplateVersion) || templates[0];
          console.log('[CplList] Found template:', template);
          if (!template) {
            console.error('Form template not found. Templates array:', templates);
            console.error('Task references templateId:', resolvedTask.formTemplateId, 'version:', resolvedTask.formTemplateVersion);
            alert(`Form template not found. The form linked to this task may have been deleted or is not accessible. Template ID: ${baseTemplateId}`);
            return;
          }

          // Normalize tabs in case API returns stringified tabs
          const normalizedTemplate: FormTemplate = {
            ...template,
            id: (template as any).id || (template as any).templateId || baseTemplateId,
            tabs: Array.isArray((template as any).tabs)
              ? (template as any).tabs
              : typeof (template as any).tabs === 'string'
                ? JSON.parse((template as any).tabs)
                : []
          };
          console.log('[CplList] Normalized template:', normalizedTemplate);
          
          // Fetch existing response if available
          console.log('[CplList] Fetching task responses...');
          this.formResponseService.getTaskResponses(
            template.id,
            this.organizationId,
            resolvedTask.projectId,
            resolvedTask.taskId
          )
            .subscribe({
              next: (responses) => {
                console.log('[CplList] Got responses:', responses);
                const existingResponse = responses?.length > 0 ? responses[0] : undefined;
                console.log('responses list ', responses);
                
                // Hide loader - form is ready to display
                this.formModalLoading = false;
                
                // Open form in XL modal
                console.log('[CplList] Opening MatDialog...');
                const dialogConfig = new MatDialogConfig();
                dialogConfig.width = '95vw';
                dialogConfig.height = '95vh';
                dialogConfig.maxWidth = '1400px';
                dialogConfig.panelClass = ['form-modal-xl'];
                dialogConfig.disableClose = false;
                dialogConfig.data = {
                  template: normalizedTemplate,
                  existingResponse,
                  organizationId: this.organizationId,
                  projectId: resolvedTask.projectId,
                  taskId: resolvedTask.taskId,
                  task: resolvedTask,
                  customerId: this.customerId,
                  context: 'customerportal-task'
                };

                const dialogRef = this.matDialog.open(FormRendererComponent, dialogConfig);
                console.log('[CplList] Dialog opened, dialogRef:', dialogRef);

                dialogRef.afterClosed()
                  .pipe(untilDestroyed(this))
                  .subscribe(result => {
                    if (result?.taskCompleted) {
                      // Task was completed via form submission
                      this.taskDone(resolvedTask);
                    }
                  });
              },
              error: (err) => {
                console.error('Error loading form response:', err);
                // Still open the form even if no draft exists
                this.formModalLoading = false;
                const dialogConfig = new MatDialogConfig();
                dialogConfig.width = '95vw';
                dialogConfig.height = '95vh';
                dialogConfig.maxWidth = '1400px';
                dialogConfig.panelClass = ['form-modal-xl'];
                dialogConfig.disableClose = false;
                dialogConfig.data = {
                  template: normalizedTemplate,
                  organizationId: this.organizationId,
                  projectId: resolvedTask.projectId,
                  taskId: resolvedTask.taskId,
                  task: resolvedTask,
                  customerId: this.customerId,
                  context: 'customerportal-task'
                };

                this.matDialog.open(FormRendererComponent, dialogConfig);
              }
            });
        },
        error: (err) => {
          console.error('[CplList] Error loading form template:', err);
          alert('Error loading form template: ' + (err.message || JSON.stringify(err)));
        },
        complete: () => {
          console.log('[CplList] Form template observable completed');
        }
      });
  }

  onFormSubmitted(formResponse: any) {
    console.log('[CplList] Form submitted:', formResponse);
    // Form was successfully submitted - task completion handled in dialog afterClosed
  }

  onFormDraftSaved(formResponse: any) {
    console.log('[CplList] Form draft saved:', formResponse);
    // Draft saved - handled automatically by FormRendererComponent
  }

  onFormCancelled() {
    console.log('[CplList] Form cancelled');
    // User closed form without submitting - handled by dialog close
  }

  onContentPrinted() {
    setTimeout(() => {
      this.contentPrinted = true;
      this.changeDetector.detectChanges();
    }, 200);
  }


  resetUploadModalState() {
    // Reset all upload modal state when modal is closed
    this.uploadedFiles.clear();

    // Clear modalTask to null, which will cause the *ngFor to not render any fileupload components
    // This effectively resets all child components
    this.modalTask = null;

    this.fileselected = false;
    this.contentPrinted = false;
    this.filelistUpdatedvalue = false;
    this.projectId = null;

    // Force Angular to detect changes to ensure the form is reset
    if (this.changeDetector) {
      this.changeDetector.detectChanges();
    }

    //console.log('Upload modal state has been reset');
  }

  resetFormModalState() {
    // No longer needed - MatDialog handles its own cleanup
    console.log('[CplList] Form modal cleanup (MatDialog handles internally)');
  }

  ngOnDestroy() {
    // Clean up event listeners to prevent memory leaks
    $('#uploadmodal').off('hidden.bs.modal');
  }

  onLoopFinished(): void {
    if (this.hasRendered) {
      return; // already handled once, do nothing
    }
    this.hasRendered = true;
    this.uieventhub.removeLoader('customerlight');
    this.isLoading = false; // hide loader
  }
}
